import { useState, useEffect, useRef } from "react";
import "./callWindow.css";
import { LiveAudioVisualizer } from "react-audio-visualize";
import placeholder from "@/assets/placeholder.png";
import retellWebClient from "@/utils/retell-web-client";
import registerCall from "@/utils/registercall";
import CallHintsDisplay from "./CallHintsDisplay";

interface RetellCallDialogProps {
  onEndCall: () => void;
  agentName: string;
  name: string;
  callHints: any;
  callHintsTable: any;
  icon: any;
  agentId: string;
  phoneNumber: string;
}

const RetellCallDialog: React.FC<RetellCallDialogProps> = ({
  onEndCall,
  agentName,
  name,
  callHints,
  callHintsTable,
  icon,
  agentId,
  phoneNumber
}) => {
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(
    null
  );
  const [stream, setStream] = useState<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const [currentDate] = useState(new Date().toLocaleDateString());
  const [isCalling, setIsCalling] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const timerRef = useRef<number | null>(null);

  const askForMicrophonePermissions = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (error) {
      console.error("Microphone permission error:", error);
      handleEndCall();
    }
  };

  const startTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    setSeconds(0);
    timerRef.current = window.setInterval(() => {
      setSeconds((prev) => prev + 1);
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const initializeCall = async () => {
    try {
      setIsConnecting(true);
      const registerCallResponse = await registerCall(agentId);

      if (registerCallResponse.call_id) {
        await retellWebClient.startCall({
          accessToken: registerCallResponse.access_token,
          sampleRate: registerCallResponse.sample_rate
        });
        setIsCalling(true);
        startTimer();
      }
    } catch (error) {
      console.error("Error starting conversation:", error);
      handleEndCall();
    }
  };

  const handleEndCall = () => {
    stopTimer();
    retellWebClient.stopCall();
    setIsCalling(false);
    setIsConnecting(false);
    onEndCall();
  };

  useEffect(() => {
    askForMicrophonePermissions();

    retellWebClient.on("conversationEnded", ({ code, reason }) => {
      setIsCalling(false);
      handleEndCall();
    });

    retellWebClient.on("error", (error) => {
      console.error("WebClient error:", error);
      setIsCalling(false);
      handleEndCall();
    });

    retellWebClient.on("agent_start_talking", () => {
      if (isConnecting) {
        setIsConnecting(false);
        startTimer();
      }
    });

    retellWebClient.on("update", (update) => {
      console.log("Please print thje update",update);
      
      if (isConnecting) {
        setIsConnecting(false);
        startTimer();
      }
    });

    return () => {
      retellWebClient.removeAllListeners();
    };
  }, [isConnecting]);

  useEffect(() => {
    const startRecording = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true
        });
        setStream(stream);

        audioContextRef.current = new AudioContext();
        const source = audioContextRef.current.createMediaStreamSource(stream);

        const highPassFilter = audioContextRef.current.createBiquadFilter();
        highPassFilter.type = "highpass";
        highPassFilter.frequency.setValueAtTime(
          400,
          audioContextRef.current.currentTime
        );

        source.connect(highPassFilter);
        const destination =
          audioContextRef.current.createMediaStreamDestination();
        highPassFilter.connect(destination);

        const mediaRecorder = new MediaRecorder(destination.stream);
        setMediaRecorder(mediaRecorder);
        mediaRecorder.start();

        mediaRecorder.onerror = (event) => {
          console.error("MediaRecorder error:", (event as ErrorEvent).error);
        };
      } catch (error) {
        console.error("Failed to access microphone:", error);
        handleEndCall();
      }
    };

    startRecording();

    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (mediaRecorder) {
        mediaRecorder.stop();
      }
      stopTimer();
    };
  }, []);

  useEffect(() => {
    initializeCall();
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const formatTime = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="modal-overlay">
      <div className="callcategory">
        <div className="calltopheader">{name}</div>
        <section className="call-window-container">
          <div className="voice-call-header">
            <div className="font-semibold text-2xl">Voice Call</div>
            <div className="call-date">{currentDate}</div>
          </div>
          <div className="voice-call-container">
            <div className="callcontainer">
              <div className="grid-wrapper">
                <div className="block-main">
                  <div className="nurix-icon-wrapper">
                    <div className="nurix-icon">
                      <img
                        className="x-icon is-small"
                        src={icon}
                        alt="AI Agent"
                      />
                    </div>
                    <div className="font-24" style={{ marginTop: "-40px" }}>
                      <b>{agentName}</b>
                    </div>
                  </div>

                  <div className="sound-visualizer-wrapper">
                    <div>
                      {mediaRecorder && (
                        <div style={{ display: "flex", gap: "3px" }}>
                          <div style={{ transform: "scaleX(-1)" }}>
                            <LiveAudioVisualizer
                              mediaRecorder={mediaRecorder}
                              width={150}
                              height={70}
                              barWidth={3}
                              gap={3}
                              barColor={"#3b82f6"}
                              fftSize={256}
                              minDecibels={-70}
                              smoothingTimeConstant={0.9}
                            />
                          </div>
                          <div>
                            <LiveAudioVisualizer
                              mediaRecorder={mediaRecorder}
                              width={150}
                              height={70}
                              barWidth={3}
                              gap={3}
                              barColor={"#3b82f6"}
                              fftSize={256}
                              minDecibels={-70}
                              smoothingTimeConstant={0.9}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="call-timer">
                      {isConnecting ? (
                        <div className="mb-1">connecting...</div>
                      ) : (
                        formatTime(seconds)
                      )}
                    </div>
                  </div>

                  <div className="image-icon-wrapper">
                    <div className="image-icon">
                      <img src={placeholder} alt="You" />
                    </div>
                    <div className="font-24" style={{ marginTop: "-20px" }}>
                      <b>You</b>
                    </div>
                  </div>
                </div>
              </div>

              <CallHintsDisplay
                callHints={callHints}
                callHintsTable={callHintsTable}
              />

              <div className="btn-wrapper-is-margin">
                <button
                  className="call-nurix-button is-red"
                  onClick={handleEndCall}
                >
                  End Call
                </button>
              </div>
              <span className="opacity-30 mt-3">
                This AI agent is a demo and may not provide fully accurate
                responses or take any actions.
              </span>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default RetellCallDialog;
